import { Box, Circle, HStack, VStack } from '@chakra-ui/layout';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  IconButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { DuotoneIcon, Icon } from '@common/components/Icon';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { RiskValueMapping } from '@services/appsync/mappings';
import { SurveyV2ObjectiveChoice } from '@services/appsync/types';
import React, { PropsWithChildren, useState } from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { useSurveyBannerModel } from '../models/SurveyBannerModel';

type OptionBoxProps = {
  selected: boolean;
  onSelect: () => void;
};

const OptionBox: React.FC<PropsWithChildren<OptionBoxProps>> = ({ children, selected, onSelect }) => {
  return (
    <Box w={'100%'} cursor="pointer">
      <Flex
        alignItems={'center'}
        boxSizing="content-box"
        onClick={onSelect}
        borderRadius="10px"
        gap="15px"
        p={['10px', '10px', '20px']}
        borderWidth={selected ? '3px' : '3px'}
        borderColor={selected ? 'green.400' : 'gray.50'}
        justifyContent="center">
        {children}
      </Flex>

      {selected && (
        <Flex mt={['-8px', '-8px', '-10px']} justifyContent="center">
          <Icon backgroundColor={'white'} icon="circle-check" color={'green.400'} />
        </Flex>
      )}
    </Box>
  );
};

type ObjectivesProps = {
  modalProps: UseDisclosureProps;
};

export const Objectives: React.FC<ObjectivesProps> = ({ modalProps }) => {
  const model = useSurveyBannerModel();
  const { investorAccount, updateObjectivesRisks } = useInvestorAccountModel();
  const [riskAccepted, setRiskAccepted] = useState<boolean | null>(null);
  const [showError, setShowError] = useState(false);
  const [objective, setObjective] = useState<SurveyV2ObjectiveChoice | null | undefined>(
    investorAccount?.non_accredited_survey_v2?.objective?.value,
  );
  const [risk, setRisk] = useState<RiskValueMapping | null | undefined>(
    investorAccount?.non_accredited_survey_v2?.risk_profile?.value,
  );

  return (
    <ModalContent>
      <ModalHeader
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        borderBottomWidth={'1px'}
        borderBottomColor={'gray.300'}>
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="chevron-left" />}
          onClick={() => model.prevStep()}
        />
        Objectives
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="times" />}
          onClick={modalProps.onClose}
        />
      </ModalHeader>
      <ModalBody>
        <Flex py={'24px'} flex={1} justifyContent={'space-between'} flexDirection={'column'} display={'flex'}>
          <VStack gap={'30px'}>
            <Box>
              <TextBlock fontSize={['14px', '14px', '20px']} textAlign="center" weight="bold" lineHeight={'20px'}>
                What is your main objective for investing with Concreit
              </TextBlock>
              <VStack mt={'24px'} gap="5px">
                <OptionBox
                  selected={objective === SurveyV2ObjectiveChoice.BALANCED}
                  onSelect={() => setObjective(SurveyV2ObjectiveChoice.BALANCED)}>
                  <Box>
                    <DuotoneIcon icon="circle-dollar-to-slot" secondaryColor="#A3A3A3" />
                  </Box>
                  <TextBlock as={'span'} fontSize={['13px', '13px', '16px']} color="emphasized" weight="bold">
                    Create an additional income stream
                  </TextBlock>
                </OptionBox>
                <OptionBox
                  selected={objective === SurveyV2ObjectiveChoice.GROWTH}
                  onSelect={() => setObjective(SurveyV2ObjectiveChoice.GROWTH)}>
                  <Box>
                    <DuotoneIcon icon="hand-holding-seedling" secondaryColor="#A3A3A3" />
                  </Box>
                  <TextBlock as={'span'} fontSize={['13px', '13px', '16px']} color="emphasized" weight="bold">
                    {`Grow my investment portfolio`}
                  </TextBlock>
                </OptionBox>
                <OptionBox
                  selected={objective === SurveyV2ObjectiveChoice.PRESERVATION}
                  onSelect={() => setObjective(SurveyV2ObjectiveChoice.PRESERVATION)}>
                  <Box>
                    <DuotoneIcon icon="piggy-bank" primaryColor="#A3A3A3" swapOpacity />
                  </Box>
                  <TextBlock as={'span'} fontSize={['13px', '13px', '16px']} color="emphasized" weight="bold">
                    Preserve my savings with minimal risk
                  </TextBlock>
                </OptionBox>
              </VStack>
              {objective === SurveyV2ObjectiveChoice.PRESERVATION && (
                <VStack mt={'10px'} gap={'5px'}>
                  <TextBlock fontSize={['13px', '13px', '16px']} textAlign="center">
                    Our objective is to protect your money, but investing in real estate involves risk and potential
                    loss of principal.
                  </TextBlock>
                  <TextBlock fontSize={['13px', '13px', '16px']} textAlign="center" fontWeight={'bold'}>
                    Are you ok with this?
                  </TextBlock>
                  <HStack justifyContent="center" alignItems="center">
                    <Circle
                      onClick={() => {
                        setRiskAccepted(false);
                      }}
                      boxSizing={'content-box'}
                      cursor={'pointer'}
                      _hover={{ borderWidth: '2px', px: '23px', py: '8px' }}
                      py={riskAccepted === false ? '8px' : '9px'}
                      px={riskAccepted === false ? '23px' : '24px'}
                      borderWidth={riskAccepted === false ? '2px' : '1px'}
                      borderColor={riskAccepted === false ? 'red.500' : 'gray.300'}>
                      <VStack>
                        <Box mb={'-0.5rem'}>
                          <Icon variant={'regular'} icon="thumbs-up" />
                        </Box>
                        <TextBlock>No</TextBlock>
                      </VStack>
                    </Circle>
                    <Circle
                      onClick={() => {
                        setRiskAccepted(true);
                      }}
                      boxSizing={'content-box'}
                      cursor={'pointer'}
                      _hover={{ borderWidth: '2px', px: '20px', py: '8px' }}
                      py={riskAccepted ? '8px' : '9px'}
                      px={riskAccepted ? '20px' : '21px'}
                      borderWidth={riskAccepted ? '2px' : '1px'}
                      borderColor={riskAccepted ? 'blue.500' : 'gray.300'}>
                      <VStack>
                        <Box mb={'-0.5rem'}>
                          <Icon variant={'regular'} icon="thumbs-up" />
                        </Box>
                        <TextBlock>Yes</TextBlock>
                      </VStack>
                    </Circle>
                  </HStack>
                  {riskAccepted === false && (
                    <Box borderWidth={'2px'} borderColor={'red.500'} px={'24px'} py={'10px'} borderRadius={'20px'}>
                      <TextBlock fontSize={'14px'} textAlign={'center'}>
                        Sorry, but if you are not able to tolerate the potential risks associated with real estate, we
                        are unable to accept you as an investor at this time.
                      </TextBlock>
                      <TextBlock fontWeight={'bold'} fontSize={'14px'} textAlign={'center'}>
                        If you would like to continue, please adjust your answer above
                      </TextBlock>
                    </Box>
                  )}
                </VStack>
              )}
            </Box>
            <Box>
              <TextBlock fontSize={['14px', '14px', '20px']} textAlign="center" weight="bold" lineHeight={'20px'}>
                What type of investments would you be comfortable with?
              </TextBlock>
              <VStack mt={'24px'} gap="5px">
                <OptionBox selected={risk === RiskValueMapping.HIGH} onSelect={() => setRisk(RiskValueMapping.HIGH)}>
                  <TextBlock as={'span'} fontSize={['13px', '13px', '16px']} color="emphasized" weight="bold">
                    High risk, high potential return
                  </TextBlock>
                </OptionBox>
                <OptionBox
                  selected={risk === RiskValueMapping.MEDIUM}
                  onSelect={() => setRisk(RiskValueMapping.MEDIUM)}>
                  <TextBlock as={'span'} fontSize={['13px', '13px', '16px']} color="emphasized" weight="bold">
                    Moderate risk, moderate potential return
                  </TextBlock>
                </OptionBox>
                <OptionBox selected={risk === RiskValueMapping.LOW} onSelect={() => setRisk(RiskValueMapping.LOW)}>
                  <TextBlock as={'span'} fontSize={['13px', '13px', '16px']} color="emphasized" weight="bold">
                    Low risk, low potential return
                  </TextBlock>
                </OptionBox>
              </VStack>
            </Box>
          </VStack>

          {showError && (
            <Alert status="error">
              <AlertIcon />
              <AlertDescription>We were unable to update your profile. Please try again</AlertDescription>
            </Alert>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          autoFocus={true}
          isLoading={model.loading}
          isDisabled={objective === SurveyV2ObjectiveChoice.PRESERVATION ? !risk || !riskAccepted : !objective || !risk}
          w={'100%'}
          rightIcon={<Icon icon="chevron-right" />}
          mt={'10px'}
          onClick={async () => {
            setShowError(false);
            if (objective && risk && objective) {
              const survey = investorAccount?.non_accredited_survey_v2;
              const answersChanged = survey?.objective?.value !== objective || survey.risk_profile?.value !== risk;
              let success = true;
              if (answersChanged) {
                model.setLoading(true);
                success = (await updateObjectivesRisks({ objectiveChoice: objective, riskProfile: risk })).success;
              }
              if (success) {
                model.nextStep();
                model.setLoading(false);
              } else {
                setShowError(true);
                model.setLoading(false);
              }
            }
          }}>
          Continue
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};
